














































































































import { InputType } from '@getaccept/lib-ui';
import PasswordValidator from '@getaccept/lib-shared/src/password-validator/PasswordValidator.vue';

import { computed, defineComponent, ref, watch } from '@vue/composition-api';
import type { AxiosError } from 'axios';
import bugsnagClient from '@getaccept/lib-shared/src/bugsnag';
import { RecaptchaAction } from '@getaccept/lib-shared/src/recaptcha/recaptcha-action';
import type { LoginResponse } from '../../api/login/types/login-response';
import { LoginService } from '../../api/login/login.service';
import { RecaptchaService } from '../../api/recaptcha/recaptcha.service';
import { getErrorMessage } from '../../helpers/error-message';
import { ErrorKey } from '../../types/enums/error-key';
import type { ErrorMessage } from '../../types/error-message';
import i18n from '../../i18n/lang';
import { PasswordValidation } from '../../helpers/password-validation';
import { PasswordExpiredErrorCode } from '../types/enums/password-expired-error-code';
import { PasswordExpiredError } from '../types/enums/password-expired-error';
import router from '../../router/router';
import { useLogin } from '../../login/composable/login.composable';

export default defineComponent({
  components: {
    PasswordValidator,
  },
  setup() {
    const { loginSuccess } = useLogin();
    const oldPassword = ref('');
    const newPassword = ref('');
    const newPasswordRepeat = ref('');
    const viewOldPassword = ref(false);
    const viewNewPassword = ref(false);
    const viewNewPasswordRepeat = ref(false);
    const oldPasswordError = ref('');
    const errorMessage = ref('');
    const passwordMatchError = ref('');
    const showValidatorError = ref(false);
    const isValidPassword = ref(false);
    const error = ref<ErrorMessage>(null);
    const loading = ref(false);

    watch(oldPassword, () => {
      oldPasswordError.value = '';
    });
    watch(newPassword, () => {
      errorMessage.value = '';
      passwordMatchError.value = '';
    });
    watch(newPasswordRepeat, () => {
      passwordMatchError.value = '';
    });

    const passwordErrorMessage = computed(
      () => errorMessage.value || passwordMatchError.value || ''
    );

    const oldPasswordInputType = computed(() => getInputType(viewOldPassword.value));
    const newPasswordInputType = computed(() => getInputType(viewNewPassword.value));
    const newPasswordRepeatInputType = computed(() => getInputType(viewNewPasswordRepeat.value));

    const getInputType = (view: boolean): InputType => (view ? InputType.Text : InputType.Password);

    const toggleViewOldPassword = () => {
      viewOldPassword.value = !viewOldPassword.value;
    };
    const toggleViewNewPassword = () => {
      viewNewPassword.value = !viewNewPassword.value;
    };

    const toggleViewNewPasswordRepeat = () => {
      viewNewPasswordRepeat.value = !viewNewPasswordRepeat.value;
    };
    const validateOldPassword = (password: string): string => {
      if (!password) {
        return i18n.t('password-is-required').toString();
      }
      return '';
    };

    const handleIsValidPassword = (isValid: boolean) => {
      isValidPassword.value = isValid;
    };

    const resetExpiredPassword = async ({
      oldPassword,
      newPassword,
      newPasswordRepeat,
    }: {
      oldPassword: string;
      newPassword: string;
      newPasswordRepeat: string;
    }) => {
      loading.value = true;
      try {
        const recaptchaToken: string = await RecaptchaService.getToken(RecaptchaAction.Login);
        const email = router.currentRoute.query.email ? `${router.currentRoute.query.email}` : '';
        const data: LoginResponse = await LoginService.expired({
          oldPassword,
          newPassword,
          newPasswordRepeat,
          email,
          recaptchaToken,
        });
        const { status, entityCount, authMethod, authEntityId }: LoginResponse = data;

        if (status !== 1) {
          resetExpiredPasswordFailed({ data });
          loading.value = false;
          return;
        }
        loginSuccess({
          entityCount,
          authMethod,
          authEntityId,
        });
      } catch (error) {
        resetExpiredPasswordFailed({ requestError: error });
      }
      loading.value = false;
    };

    const resetExpiredPasswordFailed = ({
      data,
      requestError,
    }: {
      data?: LoginResponse;
      requestError?: AxiosError;
    }) => {
      let newError: ErrorMessage;
      const errorCode: number = requestError?.response?.status;
      const errorMessage: string = data?.error;
      if (requestError) {
        if (errorCode === PasswordExpiredErrorCode.LongerBetweenAttempts) {
          newError = getErrorMessage(ErrorKey.Throttled);
        } else {
          newError = getErrorMessage(ErrorKey.Unknown);
          if (bugsnagClient) {
            bugsnagClient.notify(requestError, event => {
              event.groupingHash = requestError;
            });
          }
        }
      } else {
        switch (errorMessage) {
          case PasswordExpiredError.Throttled:
            newError = getErrorMessage(ErrorKey.Throttled);
            break;
          case PasswordExpiredError.WeakPassword:
            newError = getErrorMessage(ErrorKey.WeakPassword);
            break;
          case PasswordExpiredError.SamePassword:
            newError = getErrorMessage(ErrorKey.SamePassword);
            break;
          case PasswordExpiredError.IncorrectPassword:
            newError = getErrorMessage(ErrorKey.InvalidPassword);
            break;
          case PasswordExpiredError.UpdateFailed:
          case PasswordExpiredError.InvalidUser:
          default:
            newError = getErrorMessage(ErrorKey.Unknown);
            if (bugsnagClient) {
              bugsnagClient.notify(new Error(JSON.stringify(data)), event => {
                event.groupingHash = JSON.stringify(data);
              });
            }
        }
      }
      error.value = newError;
    };

    const handleSubmit = () => {
      showValidatorError.value = true;
      oldPasswordError.value = validateOldPassword(oldPassword.value);
      errorMessage.value = PasswordValidation.validateNewPassword(
        newPassword.value,
        isValidPassword.value
      );
      passwordMatchError.value = PasswordValidation.validateNewPasswordRepeat(
        newPassword.value,
        newPasswordRepeat.value
      );
      if (errorMessage.value || passwordMatchError.value || oldPasswordError.value) {
        return;
      }
      resetExpiredPassword({
        oldPassword: oldPassword.value,
        newPassword: newPassword.value,
        newPasswordRepeat: newPasswordRepeat.value,
      });
    };

    return {
      handleSubmit,
      handleIsValidPassword,
      error,
      loading,
      passwordErrorMessage,
      oldPassword,
      oldPasswordInputType,
      oldPasswordError,
      newPasswordInputType,
      newPasswordRepeatInputType,
      toggleViewOldPassword,
      toggleViewNewPassword,
      toggleViewNewPasswordRepeat,
      viewOldPassword,
      newPassword,
      errorMessage,
      viewNewPassword,
      newPasswordRepeat,
      viewNewPasswordRepeat,
      showValidatorError,
      isValidPassword,
      passwordMatchError,
      resetExpiredPasswordFailed,
    };
  },
});
